import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from 'useIntl';
import { useDispatch } from 'react-redux';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Label,
  Avatar,
  DateUtils,
  Icon,
  Menu,
  MenuItem,
  FileUtils,
} from 'mw-style-react';
import { DATE_FORMAT, SET_MODAL } from 'constants';
import AppUtils from '@control-front-end/utils/utils';
import mes from './intl';
import './AttachList.scss';

/**
 * Список вложений
 */
function AttachList({ list = [], canEdit = false, onDelete }) {
  const t = useIntl();
  const dispatch = useDispatch();
  const [menu, setMenu] = useState({});

  // Показать увеличенную картинку из прикрепленных файлов
  const showImage = (img) => {
    dispatch({
      type: SET_MODAL,
      payload: {
        name: 'FilePreview',
        files: list,
        data: {
          id: img.id,
          files: list,
        },
      },
    });
  };

  // Скачать файл из прикрепленных вложений
  const downloadFile = (file) => {
    const filePath =
      file.filePath || AppUtils.makeAppUrl(`/download/${file.fileName}`);
    FileUtils.download(filePath, file.title);
  };

  // Клик по иконке more
  const handleMoreClick = (id) => {
    setMenu((curMenu) => {
      return {
        ...curMenu,
        [id]: curMenu[id] ? !curMenu[id] : true,
      };
    });
  };

  // Обработка кликов по пунктам меню
  const handleMenuClick = ({ file, value }) => {
    switch (value) {
      case 'download':
        downloadFile(file);
        break;
      case 'delete':
        onDelete(file);
        break;
      default:
        break;
    }
    handleMoreClick(file.id);
  };

  // Закрыть меню
  const handleMenuClose = () => {
    setMenu({});
  };

  // Отрисовать меню для файла
  const renderMenu = (file, show) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Menu
          styleName="table__menu"
          size="small"
          width={200}
          onClick={({ value }) => handleMenuClick({ file, value })}
          onClose={handleMenuClose}
          visibility={show ? 'visible' : 'hidden'}
        >
          <MenuItem
            size="small"
            label={t(mes.download)}
            value="download"
            visibility="visible"
          />
          <MenuItem
            size="small"
            label={t(mes.remove)}
            value="delete"
            visibility={canEdit ? 'visible' : 'hidden'}
          />
        </Menu>
      </div>
    );
  };

  // Отрисовать превью файла
  const renderFilePreview = (file) => {
    if (file.type.indexOf('image') !== -1) {
      const filePath =
        file.filePath || AppUtils.makeAppUrl(`/download/${file.fileName}`);
      return (
        <div styleName="table__row__img__wrap">
          <img id={file.id} styleName="table__row__img" src={filePath} alt="" />
        </div>
      );
    }

    return (
      <div styleName="table__row__file">
        <Icon type="attach" />
      </div>
    );
  };

  const renderRows = () => {
    return list.map((file) => (
      <TableRow
        key={file.id}
        styleName="table__row"
        rowSize="small"
        onClick={() => showImage(file)}
      >
        <TableCell>{renderFilePreview(file)}</TableCell>
        <TableCell styleName="table__cell__title">
          <Label value={file.title} />
        </TableCell>
        <TableCell>
          <Label value={FileUtils.biteToSize(file.size)} />
        </TableCell>
        <TableCell>
          <Label value={DateUtils.toDate(file.createdAt, DATE_FORMAT)} />
        </TableCell>
        <TableCell>
          {file.userAvatar ? (
            <Avatar size="small" src={file.userAvatar} label={file.name} />
          ) : null}
        </TableCell>
        <TableCell styleName="table__menu__wrap">
          <Icon
            type="more"
            styleName="table__row__icon"
            onClick={(e) => {
              e.stopPropagation();
              handleMoreClick(file.id);
            }}
          />
          {renderMenu(file, menu[file.id])}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Table paddingSize="small" styleName="table">
      <TableHead styleName="table__head">
        <TableRow styleName="table__row" rowSize="small">
          <TableCell width="32" />
          <TableCell width="auto" />
          <TableCell width="120" />
          <TableCell width="180" />
          <TableCell width="50" />
          <TableCell width="20" />
        </TableRow>
      </TableHead>
      <TableBody>{renderRows()}</TableBody>
    </Table>
  );
}

AttachList.propTypes = {
  list: PropTypes.array.isRequired,
  canEdit: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
};

export default AttachList;
